/* EBONFORM — LV-style card carousel (home page works only).
   Each work swipes through two views inside its card. The arrow shows only on the card
   under the cursor. A tap or click on the picture opens the work.
   Progressive enhancement: cards stay plain links if anything here fails. */
(() => {
  /* how many views each card carries. Change this one number. */
  const MAX_VIEWS = 2;
  const CARD_SIZES = '(max-width: 749px) 50vw, (max-width: 989px) 33vw, 25vw';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const sized = (src, w) => `${src}${src.includes('?') ? '&' : '?'}width=${w}`;
  let catalogPromise = null;

  const loadCatalog = () => {
    if (!catalogPromise) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      catalogPromise = fetch(`${root}collections/all/products.json?limit=250`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((data) => {
          const map = new Map();
          (data.products || []).forEach((p) => map.set(p.handle, p.images || []));
          return map;
        })
        .catch(() => new Map());
    }
    return catalogPromise;
  };

  /* phones group cards into sideways rows; a swipeable image inside a swipeable row
     fights the finger, so put the cards back into one simple grid. */
  const flattenRows = (rail) => {
    const rows = rail.querySelectorAll('.efh2-mobile-work-row');
    if (!rows.length) return;
    const cards = Array.from(rail.querySelectorAll('.efh2-work-card'))
      .sort((a, b) => Number(a.dataset.efh2OriginalIndex || 0) - Number(b.dataset.efh2OriginalIndex || 0));
    cards.forEach((card) => { card.removeAttribute('data-efh2-mobile-hidden'); rail.appendChild(card); });
    rows.forEach((row) => row.remove());
  };

  const nearObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
          nearObserver.unobserve(entry.target);
        });
      }, { rootMargin: '300px 0px' })
    : null;

  const makeSlide = (href, label, img, hidden) => {
    const slide = document.createElement('a');
    slide.className = 'efc-slide';
    slide.href = href;
    slide.setAttribute('aria-label', label);
    if (hidden) { slide.tabIndex = -1; slide.setAttribute('aria-hidden', 'true'); }
    img.draggable = false;
    slide.appendChild(img);
    return slide;
  };

  const mount = (container, slides, title) => {
    container.classList.add('efc-media');
    const track = document.createElement('div');
    track.className = 'efc-track';
    slides.forEach((s) => track.appendChild(s));
    container.appendChild(track);

    const mkArrow = (dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `efc-arrow efc-arrow--${dir}`;
      b.setAttribute('aria-label', `${dir === 'prev' ? 'Previous' : 'Next'} image of ${title}`);
      b.innerHTML = '<span aria-hidden="true"></span>';
      container.appendChild(b);
      return b;
    };
    const prev = mkArrow('prev');
    const next = mkArrow('next');

    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'efc-dots';
    dotsWrap.setAttribute('aria-hidden', 'true');
    const dots = slides.map(() => dotsWrap.appendChild(document.createElement('i')));
    container.appendChild(dotsWrap);

    let index = 0;
    const sync = () => {
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      slides.forEach((s, i) => {
        if (i === index) { s.removeAttribute('tabindex'); s.removeAttribute('aria-hidden'); }
        else { s.tabIndex = -1; s.setAttribute('aria-hidden', 'true'); }
      });
      prev.disabled = index === 0;
      next.disabled = index === slides.length - 1;
    };
    const goTo = (i) => {
      const target = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: target * track.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
        if (i !== index) { index = i; sync(); }
      });
    }, { passive: true });
    prev.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(index - 1); });
    next.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); goTo(index + 1); });

    if ('ResizeObserver' in window) {
      let lastW = track.clientWidth;
      new ResizeObserver(() => {
        if (track.clientWidth === lastW) return;
        lastW = track.clientWidth;
        track.scrollLeft = index * track.clientWidth;
      }).observe(track);
    }
    if (nearObserver) nearObserver.observe(container);
    sync();
  };

  const enhanceHomeCard = (card, catalog) => {
    if (card.dataset.efcReady === '1') return;
    const link = card.querySelector(':scope > a');
    const figure = link && link.querySelector('figure');
    if (!link || !figure) return;
    const handle = (link.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    const images = (handle && catalog.get(decodeURIComponent(handle))) || [];
    if (images.length < 2) return;
    card.dataset.efcReady = '1';

    const title = (card.querySelector('h3')?.textContent || '').trim();
    const firstImg = figure.querySelector('img');
    const views = images.slice(0, MAX_VIEWS);

    /* the picture moves out of the link so the arrow buttons are not nested inside a link */
    card.insertBefore(figure, link);
    figure.textContent = '';
    const slides = views.map((image, i) => {
      let img;
      if (i === 0 && firstImg) {
        img = firstImg;
      } else {
        img = document.createElement('img');
        img.src = sized(image.src, 720);
        img.srcset = [360, 520, 720, 1000].map((w) => `${sized(image.src, w)} ${w}w`).join(', ');
        img.sizes = CARD_SIZES;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = firstImg ? firstImg.alt : title;
        if (image.width && image.height) { img.width = image.width; img.height = image.height; }
      }
      return makeSlide(link.href, `${title} — view ${i + 1} of ${views.length}`, img, i > 0);
    });
    mount(figure, slides, title);
  };

  const initHome = async () => {
    const rail = document.querySelector('.efh2-works__rail');
    if (!rail) return;
    if (rail.dataset.efcInit !== '1') {
      rail.dataset.efcInit = '1';
      rail.classList.add('efc-grid');
      flattenRows(rail);
      new MutationObserver(() => flattenRows(rail)).observe(rail, { childList: true });
    }
    const catalog = await loadCatalog();
    rail.querySelectorAll('.efh2-work-card').forEach((card) => enhanceHomeCard(card, catalog));
  };

  const start = () => setTimeout(initHome, 0);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
